/**
 * Clés d'accès (WebAuthn, décision 30) côté navigateur : l'appareil signe le
 * défi du serveur après déverrouillage (empreinte, visage, code). Le serveur
 * décide de tout ; ce module ne fait que relayer les cérémonies.
 *
 * Application Android/iOS (décision 16) : la vue web embarquée ne gère pas
 * WebAuthn, `passkeysSupported()` y répond non et rien n'est proposé ; un
 * greffon natif prendra le relais.
 */
import {
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
  startAuthentication,
  startRegistration,
  WebAuthnAbortService,
  WebAuthnError,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { api, ApiError } from './api.js';
import { t } from './i18n.js';

export interface PasskeyTokens { accessToken: string; refreshToken: string }

export function passkeysSupported(): boolean {
  try {
    return browserSupportsWebAuthn();
  } catch {
    return false;
  }
}

/** Appareil, pour nommer la clé d'accès comme ses utilisateurs la connaissent. */
export type PasskeyDevice = 'apple-mobile' | 'mac' | 'android' | 'windows' | 'autre';

export function passkeyDevice(ua = navigator.userAgent, touchPoints = navigator.maxTouchPoints ?? 0): PasskeyDevice {
  if (/iPhone|iPad|iPod/u.test(ua)) return 'apple-mobile';
  // L'iPad se présente comme un Mac, mais tactile.
  if (/Macintosh/u.test(ua)) return touchPoints > 1 ? 'apple-mobile' : 'mac';
  if (/Android/u.test(ua)) return 'android';
  if (/Windows/u.test(ua)) return 'windows';
  return 'autre';
}

/**
 * Nom du déverrouillage de l'appareil (24/10/2026) : « Face ID ou Touch ID » sur
 * iPhone et iPad, « Touch ID » sur Mac, « l'empreinte » sur Android, « Windows
 * Hello » sur PC. Sans lecteur intégré, la clé passe par le téléphone ou une clé
 * physique : « une clé d'accès ».
 */
export function passkeyMethodName(platform: boolean, device: PasskeyDevice = passkeyDevice()): string {
  if (!platform) return t('auth.methode.cle');
  return t(`auth.methode.${device === 'apple-mobile' ? 'apple' : device === 'autre' ? 'cle' : device}`);
}

/** Lecteur intégré (Face ID, Touch ID, empreinte, Windows Hello) prêt sur cet appareil ? */
export async function platformPasskeyAvailable(): Promise<boolean> {
  try {
    return passkeysSupported()
      && await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/** Proposition des clés dans le champ identifiant (saisie automatique du navigateur). */
export async function passkeyAutofillSupported(): Promise<boolean> {
  try {
    return passkeysSupported() && await browserSupportsWebAuthnAutofill();
  } catch {
    return false;
  }
}

/** Abandonne la demande en cours (suggestion discrète quittée avec l'écran). */
export function cancelPasskeyRequest(): void {
  WebAuthnAbortService.cancelCeremony();
}

/** Abandon par la personne (fenêtre fermée, délai) : rien à signaler. */
export function passkeyCancelled(error: unknown): boolean {
  if (error instanceof WebAuthnError) return error.code === 'ERROR_CEREMONY_ABORTED' || error.name === 'NotAllowedError' || error.name === 'AbortError';
  return error instanceof DOMException && (error.name === 'NotAllowedError' || error.name === 'AbortError');
}

/** Message à afficher pour un échec : celui du serveur, sinon une phrase simple. */
export function passkeyErrorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (passkeyCancelled(error)) return t('compte.clesAcces.annule');
  if (error instanceof WebAuthnError && error.code === 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED') {
    return t('compte.clesAcces.dejaEnregistree');
  }
  return t('compte.clesAcces.echec');
}

/** Ajoute une clé d'accès au compte (l'API demande d'abord de confirmer son identité). */
export async function registerPasskey(label: string): Promise<{ id: string; label: string | null }> {
  const { options, challengeToken } = await api<{
    options: PublicKeyCredentialCreationOptionsJSON; challengeToken: string;
  }>('/auth/passkeys/register-options', { method: 'POST', body: {} });
  const response = await startRegistration({ optionsJSON: options });
  return api('/auth/passkeys/register', { method: 'POST', body: { challengeToken, response, label } });
}

/**
 * Double authentification obligatoire configurée par une clé d'accès (issue #138) :
 * Face ID ou l'empreinte à la place de l'appli de codes, à la première connexion.
 */
export async function setupMfaWithPasskey(setupToken: string): Promise<PasskeyTokens> {
  const { options, challengeToken } = await api<{
    options: PublicKeyCredentialCreationOptionsJSON; challengeToken: string;
  }>('/auth/mfa/setup/passkey-options', { method: 'POST', body: { setupToken } });
  const response = await startRegistration({ optionsJSON: options });
  return api<PasskeyTokens>('/auth/mfa/setup/passkey', { method: 'POST', body: { setupToken, challengeToken, response } });
}

/**
 * Connexion par clé d'accès. `autofill` : demande discrète, servie quand la
 * personne choisit une clé dans les suggestions du champ identifiant.
 */
export async function passkeyLogin(autofill = false): Promise<PasskeyTokens> {
  const { options, challengeToken } = await api<{
    options: PublicKeyCredentialRequestOptionsJSON; challengeToken: string;
  }>('/auth/passkeys/login-options', { method: 'POST', body: {} });
  const response = await startAuthentication({ optionsJSON: options, useBrowserAutofill: autofill });
  return api<PasskeyTokens>('/auth/passkeys/login', {
    method: 'POST', body: { challengeToken, response, platform: 'web' },
  });
}

/** Vérification d'identité de cette session par clé d'accès ; rend le jeton d'accès rafraîchi. */
export async function passkeyStepUp(): Promise<string> {
  const { options, challengeToken } = await api<{
    options: PublicKeyCredentialRequestOptionsJSON; challengeToken: string;
  }>('/auth/step-up/passkey-options', { method: 'POST', body: {} });
  const response = await startAuthentication({ optionsJSON: options });
  const result = await api<{ accessToken: string }>('/auth/step-up/passkey', {
    method: 'POST', body: { challengeToken, response },
  });
  return result.accessToken;
}
